import { ErrorMessage } from '@hookform/error-message';
import {
  addDays,
  format,
  isSameDay,
  isSaturday,
  isSunday,
  parse,
} from 'date-fns';
import { ChangeEventHandler, FC } from 'react';
import { useFormContext } from 'react-hook-form';
import Caption, {
  CaptionsUnderForm,
} from '../../../../../components/common/Caption';
import CheckBox from '../../../../../components/common/CheckBox';
import DateInput from '../../../../../components/common/DateInput';
import FormItem from '../../../../../components/common/FormItem';
import Input from '../../../../../components/common/Input';
import RadioButton from '../../../../../components/common/RadioButton';
import Select, { Option } from '../../../../../components/common/Select';
import {
  date,
  dateOutOfRangeErrorMessage,
  timeOutOfRangeErrorMessage,
  halfWidth,
  invalidTimeErrorMessage,
  numeric,
  required,
} from '../../../../../message';
import {
  CollectionConstraintType,
  ContractFormData,
  Weekday,
} from '../../../../../types/contracts';
import { validateDateWithHyphen } from '../../../../../validators/date';
import {
  correctTimeMinutes,
  validateTimeMinutes,
} from '../../../../../validators/time';
import SectionHead from '../../SectionHead';
import styles from './styles.module.css';
import { parseNumericString } from '../../../../../services/parseNumericString';

const TypedFormItem = FormItem<ContractFormData>;
const setValueOpt = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouched: true,
};
type CollectionConstraintKeys = keyof Pick<
  ContractFormData,
  | 'collectionConstraintTypeId'
  | 'collectionConstraintWeekDays'
  | 'collectionConstraintDateStart'
  | 'collectionConstraintDateEnd'
  | 'collectionConstraintTimeStart'
  | 'collectionConstraintTimeEnd'
  | 'collectionEstimatedTime'
  | 'goesStraight'
>;
const labels: Record<CollectionConstraintKeys, string> = {
  collectionConstraintTypeId: '回収日制約',
  collectionConstraintWeekDays: '曜日',
  collectionConstraintDateStart: '回収日(開始)',
  collectionConstraintDateEnd: '回収日(終了)',
  collectionConstraintTimeStart: '予定時間(開始)',
  collectionConstraintTimeEnd: '予定時間(終了)',
  collectionEstimatedTime: '作業時間',
  goesStraight: '処分場',
};
const weekDays: { id: number; name: Weekday }[] = [
  { id: 1, name: '月' },
  { id: 2, name: '火' },
  { id: 3, name: '水' },
  { id: 4, name: '木' },
  { id: 5, name: '金' },
  { id: 6, name: '土' },
  { id: 7, name: '日' },
];
const getWeekDaysIdByName = (name: Weekday) => {
  const found = weekDays.find((elem) => elem.name === name);
  if (!found) {
    throw new Error(
      "cannot find weekDays' element which has the same name as given"
    );
  }
  return found.id;
};
const fitVariant = ['fit' as const];
const fitErrorVariant = ['fit' as const, 'error' as const];
const goesStraightOptions: Option[] = [
  {
    label: '直行する',
    value: 'true',
  },
  {
    label: '積替保管',
    value: 'false',
  },
];
const collectionConstraintSpotDateLabel = '回収日';
const doesDateRangeIncludeOnlyWeekend = (startDate: Date, endDate: Date) => {
  const nextDateOfStart = addDays(startDate, 1);
  if (isSaturday(startDate) && isSameDay(nextDateOfStart, endDate)) {
    return true;
  }
  const isStartDateWeekend = isSaturday(startDate) || isSunday(startDate);
  if (isStartDateWeekend) {
    return isSameDay(startDate, endDate);
  }

  return false;
};

const CollectionConstraintForm: FC<{
  constraintTypes: CollectionConstraintType[] | undefined;
}> = ({ constraintTypes }) => {
  const {
    register,
    setValue,
    getValues,
    formState,
    watch,
    resetField,
    trigger,
    getFieldState,
    clearErrors,
  } = useFormContext<ContractFormData>();
  const getVariant = (key: CollectionConstraintKeys) => {
    if (
      key === 'collectionConstraintTimeEnd' &&
      getFieldState('collectionConstraintTimeStart')?.error?.type ===
        'dateValidation'
    )
      return fitErrorVariant;
    if (formState.errors[key]) {
      return fitErrorVariant;
    }
    return fitVariant;
  };

  // 回収情報: 回収日制約 のハンドラセットアップ
  const { name: collectionConstraintTypeName } = register(
    'collectionConstraintTypeId',
    {
      required: required(labels['collectionConstraintTypeId']),
    }
  );
  const collectionConstraintTypeId = watch('collectionConstraintTypeId');
  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = Number(e.target.value);
    setValue('collectionConstraintTypeId', value, setValueOpt);
    clearErrors('collectionConstraintDateStart');
  };
  // 回収日制約がスポットになったとき、回収日の終了を空にする
  watch(({ collectionConstraintTypeId }, { name }) => {
    if (
      !(
        name === 'collectionConstraintTypeId' &&
        collectionConstraintTypeId === /* 「スポット」 */ 2
      )
    )
      return;
    resetField('collectionConstraintDateEnd');
  });

  // 回収情報: 曜日 のハンドラセットアップ
  const { name: weekDaysName } = register('collectionConstraintWeekDays', {
    required:
      getValues('collectionConstraintTypeId') === /* 「定期」 */ 1 &&
      required(labels['collectionConstraintWeekDays']),
  });
  const constraintWeekDays = getValues('collectionConstraintWeekDays');
  const onCheckWeekDay = (value: number) => {
    const hasValue = constraintWeekDays.includes(value);
    if (hasValue) {
      setValue(
        weekDaysName,
        constraintWeekDays.filter((d) => d !== value),
        setValueOpt
      );
    } else {
      setValue(weekDaysName, [...constraintWeekDays, value], setValueOpt);
    }
  };

  // 回収情報: 処分場 のハンドラセットアップ
  const { name: goesStraightName } = register('goesStraight');
  const goesStraight = getValues(goesStraightName);
  const onChangeGoesStraight: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue(goesStraightName, e.target.value === 'true', setValueOpt);
  };

  const getConstraintDateStartLabel = (): string => {
    if (collectionConstraintTypeId === /* 「スポット」 */ 2) {
      return collectionConstraintSpotDateLabel;
    }
    return labels.collectionConstraintDateStart;
  };
  const collectionConstraintDateStart = watch('collectionConstraintDateStart');
  register('collectionConstraintDateStart', {
    required: required(getConstraintDateStartLabel()),
    validate: {
      date: (v) =>
        validateDateWithHyphen(v) || date(labels.collectionConstraintDateStart),
    },
  });
  const collectionConstraintDateEnd = watch('collectionConstraintDateEnd');
  register('collectionConstraintDateEnd', {
    required:
      collectionConstraintTypeId !== /* 「スポット」 */ 2 &&
      required(labels.collectionConstraintDateEnd),
    validate: {
      date: (v) =>
        collectionConstraintTypeId === /* 「スポット」 */ 2 ||
        validateDateWithHyphen(v) ||
        date(labels.collectionConstraintDateEnd),
      dateValidation: () =>
        collectionConstraintTypeId === /* 「スポット」 */ 2 ||
        endDateValidation() ||
        dateOutOfRangeErrorMessage,
    },
  });

  const endDateValidation = () => {
    return (
      getValues().collectionConstraintDateStart === '' ||
      getValues().collectionConstraintDateEnd === '' ||
      new Date(getValues().collectionConstraintDateStart) <=
        new Date(getValues().collectionConstraintDateEnd)
    );
  };

  const startTimeValidation = () => {
    return (
      getValues().collectionConstraintTimeStart === '' ||
      getValues().collectionConstraintTimeEnd === '' ||
      parse(getValues().collectionConstraintTimeStart, 'HH:mm', new Date()) <
        parse(getValues().collectionConstraintTimeEnd, 'HH:mm', new Date())
    );
  };

  return (
    <div>
      <SectionHead>回収情報</SectionHead>
      <div className={styles.itemContainer}>
        <TypedFormItem
          label={labels.collectionConstraintTypeId}
          name="collectionConstraintTypeId"
        >
          <div className={styles.itemRow}>
            {(constraintTypes || []).map((type) => (
              <div
                className={styles.item}
                key={type.collectionConstraintTypeId}
              >
                <RadioButton
                  name={collectionConstraintTypeName}
                  onChange={onChange}
                  value={`${type.collectionConstraintTypeId}`}
                  checked={
                    getValues('collectionConstraintTypeId') ===
                    type.collectionConstraintTypeId
                  }
                  label={type.collectionConstraintType}
                />
              </div>
            ))}
          </div>
        </TypedFormItem>
      </div>
      {getValues('collectionConstraintTypeId') === /* 「定期」 */ 1 && (
        <div className={styles.itemContainer}>
          <TypedFormItem
            label={labels.collectionConstraintWeekDays}
            name={weekDaysName}
            noLabelWrap
          >
            <div className={styles.itemRow}>
              {weekDays.map((day) => (
                <div className={styles.item} key={day.id}>
                  <CheckBox
                    label={day.name}
                    value={day.id}
                    onChange={() => onCheckWeekDay(day.id)}
                    checked={constraintWeekDays.includes(day.id)}
                  />
                </div>
              ))}
            </div>
          </TypedFormItem>
          <CaptionsUnderForm>
            <ConfirmationCaptionMessageUnderWeekdays />
          </CaptionsUnderForm>
        </div>
      )}
      <div className={styles.itemContainer}>
        <div className={styles.itemRow}>
          <TypedFormItem
            label={`回収日${
              getValues('collectionConstraintTypeId') !== /* 「スポット」 */ 2
                ? '(開始~終了)'
                : ''
            }`}
            noLabelWrap={true}
          >
            <div className={styles.itemRow}>
              <DateInput
                value={
                  collectionConstraintDateStart
                    ? new Date(collectionConstraintDateStart)
                    : undefined
                }
                placeholder={
                  getValues('collectionConstraintTypeId') !==
                  /* 「スポット」 */ 2
                    ? '開始日'
                    : collectionConstraintSpotDateLabel
                }
                variants={
                  getFieldState('collectionConstraintDateEnd')?.error?.type ===
                    'dateValidation' ||
                  formState.errors['collectionConstraintDateStart']
                    ? ['error']
                    : []
                }
                onChange={(day) => {
                  setValue(
                    'collectionConstraintDateStart',
                    day ? format(day, 'yyyy-MM-dd') : '',
                    { shouldValidate: true }
                  );
                  trigger([
                    'collectionConstraintDateStart',
                    'collectionConstraintDateEnd',
                  ]);
                }}
                onBlur={() => trigger('collectionConstraintDateStart')}
              />
              {getValues('collectionConstraintTypeId') !==
                /* 「スポット」 */ 2 && (
                <>
                  <span className={styles.tilda}>~</span>
                  <DateInput
                    value={
                      collectionConstraintDateEnd
                        ? new Date(collectionConstraintDateEnd)
                        : undefined
                    }
                    placeholder="終了日"
                    variants={
                      formState.errors['collectionConstraintDateEnd']
                        ? ['error']
                        : []
                    }
                    onChange={(day) => {
                      setValue(
                        'collectionConstraintDateEnd',
                        day ? format(day, 'yyyy-MM-dd') : '',
                        { shouldValidate: true }
                      );
                      trigger([
                        'collectionConstraintDateStart',
                        'collectionConstraintDateEnd',
                      ]);
                    }}
                    onBlur={() => trigger('collectionConstraintDateEnd')}
                  />
                </>
              )}
            </div>
          </TypedFormItem>
          <TypedFormItem label="到着予定時間" optional>
            <div className={styles.itemRow}>
              <div className={styles.time}>
                <Input
                  {...register('collectionConstraintTimeStart', {
                    validate: {
                      pattern: (v) =>
                        v === '' ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.collectionConstraintTimeStart),
                      time: (v) =>
                        v === '' ||
                        validateTimeMinutes(v) ||
                        invalidTimeErrorMessage,
                      dateValidation: () =>
                        startTimeValidation() || timeOutOfRangeErrorMessage,
                    },
                    onBlur: (e) => {
                      setValue(
                        'collectionConstraintTimeStart',
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger([
                        'collectionConstraintTimeStart',
                        'collectionConstraintTimeEnd',
                      ]);
                    },
                  })}
                  variants={getVariant('collectionConstraintTimeStart')}
                  placeholder="10:00"
                />
              </div>
              <span className={styles.tilda}>~</span>
              <div className={styles.time}>
                <Input
                  {...register('collectionConstraintTimeEnd', {
                    validate: {
                      pattern: (v) =>
                        v === '' ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.collectionConstraintTimeEnd),
                      time: (v) =>
                        v === '' ||
                        validateTimeMinutes(v) ||
                        invalidTimeErrorMessage,
                    },
                    onBlur: (e) => {
                      setValue(
                        'collectionConstraintTimeEnd',
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger([
                        'collectionConstraintTimeStart',
                        'collectionConstraintTimeEnd',
                      ]);
                    },
                  })}
                  variants={getVariant('collectionConstraintTimeEnd')}
                  placeholder="12:00"
                />
              </div>
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.collectionEstimatedTime}>
            <div className={styles.itemRow}>
              <div className={styles.time} style={{ textAlign: 'right' }}>
                <Input
                  {...register('collectionEstimatedTime', {
                    required: required(labels.collectionEstimatedTime),
                    pattern: {
                      value: /^\d+$/,
                      message: numeric('作業時間'),
                    },
                    onBlur(e) {
                      const value = e.target.value;
                      const formatted = parseNumericString(value);
                      if (Number.isNaN(formatted)) {
                        return;
                      }
                      setValue('collectionEstimatedTime', formatted, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    },
                  })}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  variants={[...getVariant('collectionEstimatedTime'), 'right']}
                  placeholder="30"
                />
              </div>
              <span className={styles.minute}>分</span>
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.goesStraight}>
            <div className={styles.itemRow}>
              <div className={styles.goesStraight}>
                <Select
                  options={goesStraightOptions}
                  value={`${goesStraight}`}
                  onChange={onChangeGoesStraight}
                  variants={['fit']}
                ></Select>
              </div>
            </div>
          </TypedFormItem>
        </div>
        <CaptionsUnderForm>
          {(
            [
              'collectionConstraintDateStart',
              'collectionConstraintDateEnd',
              'collectionConstraintTimeStart',
              'collectionConstraintTimeEnd',
              'collectionEstimatedTime',
              'goesStraight',
            ] satisfies CollectionConstraintKeys[]
          ).map((name) => (
            <ErrorMessage
              key={name}
              name={name}
              render={({ message }) => (
                <Caption variants={['error']}>{message}</Caption>
              )}
            />
          ))}
          <ConfirmationCaptionMessageUnderDateStart />
        </CaptionsUnderForm>
      </div>
    </div>
  );
};

const ConfirmationCaptionMessageUnderWeekdays: FC = () => {
  const { getValues } = useFormContext<ContractFormData>();
  if (!getValues('goesStraight')) {
    return null;
  }

  const constraintType = getValues('collectionConstraintTypeId');
  if (constraintType === /* 定期 */ 1) {
    const constraintWeekdays = getValues('collectionConstraintWeekDays');
    const toDisplayMessage = [
      getWeekDaysIdByName('土'),
      getWeekDaysIdByName('日'),
    ].some((id) => constraintWeekdays.includes(id));
    return toDisplayMessage ? (
      <Caption variants={['strongNotation']}>
        曜日に土日が指定されています。指定した曜日に処分場が営業しているか確認ください。
      </Caption>
    ) : null;
  }
  return null;
};

const ConfirmationCaptionMessageUnderDateStart: FC = () => {
  const { getValues } = useFormContext<ContractFormData>();
  if (!getValues('goesStraight')) {
    return null;
  }

  const constraintType = getValues('collectionConstraintTypeId');
  if (constraintType === /* スポット */ 2) {
    const constraintDay = new Date(getValues('collectionConstraintDateStart'));
    const toDisplayMessage =
      isSaturday(constraintDay) || isSunday(constraintDay);
    return toDisplayMessage ? (
      <Caption variants={['strongNotation']}>
        回収日に土日が指定されています。指定した曜日に処分場が営業しているか確認ください。
      </Caption>
    ) : null;
  } else if (constraintType === /* 期間 */ 3) {
    const constraintDateStart = new Date(
      getValues('collectionConstraintDateStart')
    );
    const constraintDateEnd = new Date(
      getValues('collectionConstraintDateEnd')
    );
    const toDisplayMessage = doesDateRangeIncludeOnlyWeekend(
      constraintDateStart,
      constraintDateEnd
    );
    return toDisplayMessage ? (
      <Caption variants={['strongNotation']}>
        回収日(開始~終了)が土日のみで設定されています。平日(月~金)を含めた期間を指定してください。
      </Caption>
    ) : null;
  }

  return null;
};

export default CollectionConstraintForm;
